/**
 * The media a chapter's HTML refers to, as absolute CDN URLs.
 *
 * Nothing in the reader payload enumerates a chapter's images; they exist
 * only as relative references inside the chapter HTML. So the buffer
 * scheduler parses each chapter's HTML once it has been fetched and asks for
 * whatever it finds — the same resolution the reader performs in
 * `rewriteFragmentUrls`, minus the offline redirect, because what is wanted
 * here is the CDN identity of each asset, not where it currently lives.
 *
 * Only media is collected: `src`, `poster`, `srcset` and `object[data]`.
 * Links are navigation, not assets. Inline `data:` URLs are already local
 * and anything executable is skipped for the same reason the reader skips it.
 */

const SKIPPED_SCHEMES = new Set(['data:', 'javascript:', 'vbscript:', 'blob:', 'about:']);

const resolveMediaUrl = (value: string, baseUrl: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const url = new URL(trimmed, baseUrl);
    if (SKIPPED_SCHEMES.has(url.protocol)) {
      return null;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }
    url.hash = '';
    return url.toString();
  } catch {
    return null;
  }
};

/** The URL half of each srcset candidate; descriptors are dropped. */
const srcSetUrls = (value: string): string[] => {
  const urls: string[] = [];
  let position = 0;
  while (position < value.length) {
    while (position < value.length && (value[position] === ',' || /\s/.test(value[position]))) position += 1;
    if (position >= value.length) break;
    const urlStart = position;
    const isDataUrl = value.slice(position, position + 5).toLowerCase() === 'data:';
    while (position < value.length && !/\s/.test(value[position]) && (isDataUrl || value[position] !== ',')) {
      position += 1;
    }
    urls.push(value.slice(urlStart, position));
    while (position < value.length && value[position] !== ',') position += 1;
    if (position < value.length && value[position] === ',') position += 1;
  }
  return urls;
};

/**
 * Every media URL in `html`, resolved against `baseUrl` (the chapter's own
 * URL), absolute, deduplicated, in document order.
 *
 * Parsed into an inert template, so nothing in the HTML runs.
 */
export const collectChapterMediaUrls = (html: string, baseUrl: string): string[] => {
  const template = document.createElement('template');
  template.innerHTML = html;

  const found = new Set<string>();
  const add = (value: string | null) => {
    if (value === null) return;
    const resolved = resolveMediaUrl(value, baseUrl);
    if (resolved) found.add(resolved);
  };

  for (const element of Array.from(template.content.querySelectorAll<HTMLElement>('*'))) {
    if (element.hasAttribute('src')) add(element.getAttribute('src'));
    if (element.hasAttribute('poster')) add(element.getAttribute('poster'));
    if (element.tagName === 'OBJECT' && element.hasAttribute('data')) add(element.getAttribute('data'));
    if (element.hasAttribute('srcset')) {
      for (const candidate of srcSetUrls(element.getAttribute('srcset') ?? '')) add(candidate);
    }
  }
  return Array.from(found);
};
